import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { resolveMediaUrl } from "../lib/media";
import { postUrl } from "../lib/urls";
import SEO from "../components/SEO";
import ShareButtons from "../components/ShareButtons";
import CategorySidebar from "../components/CategorySidebar";

export default function Archive({ type }) {
  const { slug } = useParams();
  const { categories, tags, posts, postsByCategory, postsByTag } = useContent();
  const isCategory = type === "category";
  const meta = isCategory ? categories.find((c) => c.slug === slug) : tags.find((t) => t.slug === slug);
  if (!meta) return <Navigate to="/destinations" replace />;

  const list = isCategory ? postsByCategory(slug) : postsByTag(slug);

  return (
    <main data-testid={`archive-page-${type}`} className="bg-[#FDFBF7] min-h-screen">
      <SEO
        title={`${meta.name} — ${isCategory ? "Category" : "Tag"}`}
        description={`${list.length} travel ${list.length === 1 ? "story" : "stories"} from the ${meta.name} archive.`}
        path={`/${isCategory ? "category" : "tag"}/${slug}`}
      />
      <section className="pt-36 pb-14 lg:pt-44 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/destinations" data-testid="archive-back" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-stone-600 hover:text-[#9c4122]">
            <ArrowLeft size={14} /> All destinations
          </Link>
          <p className="kicker mt-8">{isCategory ? "Category" : "Tag"}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.98] mt-4">
            {meta.name}<span className="text-[#9c4122]">.</span>
          </h1>
          <p className="mt-5 text-stone-600 max-w-2xl">
            <span data-testid="archive-count">{list.length}</span> {list.length === 1 ? "story" : "stories"}
          </p>
          <div className="mt-8">
            <ShareButtons title={`${meta.name} · Globezing`} description={`Travel stories from ${meta.name}.`} />
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-14">
            {isCategory ? (
              <CategorySidebar
                categories={categories}
                activeSlug={slug}
                totalPosts={posts.length}
                mode="link"
                heading="Categories"
              />
            ) : (
              <div className="hidden lg:block" />
            )}

            <div>
              {list.length === 0 ? (
                <div className="py-24 text-center border border-dashed border-stone-300">
                  <p className="font-serif text-2xl text-stone-700">No stories imported here yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-14">
                  {list.map((post, i) => (
                    <motion.div key={post.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.7, delay: (i % 6) * 0.08 }}>
                      <Link to={postUrl(post)} data-testid={`archive-card-${post.slug}`} className="group block">
                        <div className="relative aspect-[4/5] overflow-hidden bg-stone-200">
                          <img src={resolveMediaUrl(post.hero)} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
                          <span className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/90 text-stone-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                            <ArrowUpRight size={16} />
                          </span>
                        </div>
                        <div className="mt-5">
                          <p className="kicker text-stone-500">{post.kicker} · {post.readTime} min</p>
                          <h3 className="font-serif text-2xl font-normal leading-snug mt-2 text-stone-900 group-hover:text-[#9c4122] transition-colors">{post.title}</h3>
                          <p className="mt-2 text-stone-600 text-sm leading-relaxed line-clamp-2">{post.excerpt}</p>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
